import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Paper,
  Stack,
  Avatar,
  Chip,
  InputAdornment,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert as MuiAlert,
  Collapse,
  TextField as MuiTextField,
} from '@mui/material';
import {
  Search,
  PersonOutline,
  Phone,
  Email,
  ArrowBack,
  ExitToApp,
  Close,
  ExpandMore,
  FamilyRestroom,
  MedicalServices,
  Favorite,
  EventNote,
  Psychology,
  History,
  Warning,
  CheckCircle,
  AccessTime,
  PlayArrow,
  SummarizeOutlined,
  Article,
  ExpandLess,
  Edit,
  Save,
  Home,
} from '@mui/icons-material';
import { WaveIcon } from '../icons/SvgIcons';
import { useAuthStore } from '../../stores/authStore';
import { apiRequest } from '../../utils/api';
import { MobileBottomNav } from '../common/MobileBottomNav';

const CATEGORY_CONFIG = {
  family: { label: 'Familia', icon: <FamilyRestroom sx={{ fontSize: 18 }} />, color: '#7C3AED', bg: '#F5F3FF' },
  personal: { label: 'Datos personales', icon: <PersonOutline sx={{ fontSize: 18 }} />, color: '#0891B2', bg: '#ECFEFF' },
  medical: { label: 'Salud', icon: <MedicalServices sx={{ fontSize: 18 }} />, color: '#DC2626', bg: '#FEF2F2' },
  preferences: { label: 'Preferencias', icon: <Favorite sx={{ fontSize: 18 }} />, color: '#2563EB', bg: '#EFF6FF' },
  activity: { label: 'Actividad', icon: <EventNote sx={{ fontSize: 18 }} />, color: '#059669', bg: '#F0FDF4' },
  mood: { label: 'Estado de ánimo', icon: <Psychology sx={{ fontSize: 18 }} />, color: '#D97706', bg: '#FEF3C7' },
  event: { label: 'Evento', icon: <EventNote sx={{ fontSize: 18 }} />, color: '#9333EA', bg: '#FAF5FF' },
  symptom: { label: 'Síntoma', icon: <Warning sx={{ fontSize: 18 }} />, color: '#DC2626', bg: '#FEF2F2' },
  // Legacy categories
  events: { label: 'Eventos', icon: <EventNote sx={{ fontSize: 18 }} />, color: '#D97706', bg: '#FEF3C7' },
  daily_life: { label: 'Vida diaria', icon: <Psychology sx={{ fontSize: 18 }} />, color: '#059669', bg: '#F0FDF4' },
};

export const PatientSearch = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [search, setSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientProfile, setPatientProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileTab, setProfileTab] = useState(0);

  // Expandable call detail
  const [expandedCallId, setExpandedCallId] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});  // { callId: { loading, notes } }
  const [expandedVideoUrl, setExpandedVideoUrl] = useState({});  // { callId: url }
  const [videoLoading, setVideoLoading] = useState(null);

  // Patient data editing
  const [editingPatient, setEditingPatient] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingPatient, setSavingPatient] = useState(false);

  const startEditing = () => {
    if (!patientProfile) return;
    setEditForm({
      first_name: patientProfile.first_name || '',
      last_name: patientProfile.last_name || '',
      phone: patientProfile.phone || '',
      country: patientProfile.country || 'Uruguay',
      city: patientProfile.city || '',
      address: patientProfile.address || '',
      date_of_birth: patientProfile.medical.date_of_birth || '',
      blood_type: patientProfile.medical.blood_type || '',
      emergency_contact_name: patientProfile.medical.emergency_contact_name || '',
      emergency_contact_phone: patientProfile.medical.emergency_contact_phone || '',
      allergies: (patientProfile.medical.allergies || []).join(', '),
      chronic_conditions: (patientProfile.medical.chronic_conditions || []).join(', '),
      current_medications: (patientProfile.medical.current_medications || []).join(', '),
    });
    setEditingPatient(true);
  };

  const savePatientData = async () => {
    setSavingPatient(true);
    try {
      const splitList = (s) => s ? s.split(',').map(x => x.trim()).filter(Boolean) : [];
      await apiRequest(`/patients/${patientProfile.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          first_name: editForm.first_name || undefined,
          last_name: editForm.last_name || undefined,
          phone: editForm.phone || undefined,
          country: editForm.country || undefined,
          city: editForm.city || undefined,
          address: editForm.address || undefined,
          date_of_birth: editForm.date_of_birth || undefined,
          blood_type: editForm.blood_type || undefined,
          emergency_contact_name: editForm.emergency_contact_name || undefined,
          emergency_contact_phone: editForm.emergency_contact_phone || undefined,
          allergies: splitList(editForm.allergies),
          chronic_conditions: splitList(editForm.chronic_conditions),
          current_medications: splitList(editForm.current_medications),
        }),
      });
      setEditingPatient(false);
      // Reload profile
      const data = await apiRequest(`/patients/${patientProfile.id}`);
      setPatientProfile(data.patient);
    } catch (e) {
      console.error('Error saving patient:', e);
      alert('Error al guardar los cambios.');
    } finally {
      setSavingPatient(false);
    }
  };

  const searchPatients = useCallback(async (query) => {
    setLoading(true);
    try {
      const params = query ? `?search=${encodeURIComponent(query)}` : '';
      const data = await apiRequest(`/patients/search${params}`);
      setPatients(data.patients || []);
    } catch (e) {
      console.error('Error searching patients:', e);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    searchPatients('');
  }, [searchPatients]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      searchPatients(search);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search, searchPatients]);

  const openPatientProfile = async (patient) => {
    setSelectedPatient(patient);
    setProfileLoading(true);
    setProfileTab(0);
    try {
      const data = await apiRequest(`/patients/${patient.id}`);
      setPatientProfile(data.patient);
    } catch (e) {
      console.error('Error loading patient profile:', e);
      setPatientProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const closeProfile = () => {
    setSelectedPatient(null);
    setPatientProfile(null);
    setExpandedCallId(null);
    setExpandedNotes({});
    setExpandedVideoUrl({});
  };

  const toggleCallExpand = async (call) => {
    if (expandedCallId === call.id) {
      setExpandedCallId(null);
      return;
    }
    setExpandedCallId(call.id);
    // Fetch notes if not cached
    if (!expandedNotes[call.id]) {
      setExpandedNotes(prev => ({ ...prev, [call.id]: { loading: true, notes: [] } }));
      try {
        const data = await apiRequest(`/calls/${call.id}/notes`);
        setExpandedNotes(prev => ({ ...prev, [call.id]: { loading: false, notes: data.notes || [] } }));
      } catch (e) {
        setExpandedNotes(prev => ({ ...prev, [call.id]: { loading: false, notes: [] } }));
      }
    }
  };

  const loadVideoForCall = async (call) => {
    if (expandedVideoUrl[call.id]) return; // already loaded or marked as none
    setVideoLoading(call.id);
    try {
      const data = await apiRequest(`/calls/${call.id}/recordings`);
      const recs = data.recordings || [];
      if (recs.length > 0) {
        const urlData = await apiRequest(`/calls/${call.id}/recordings/${recs[0].id}/url`);
        if (urlData.url) {
          setExpandedVideoUrl(prev => ({ ...prev, [call.id]: urlData.url }));
          return;
        }
      }
      // No recording found
      setExpandedVideoUrl(prev => ({ ...prev, [call.id]: 'none' }));
    } catch (e) {
      console.error('Error fetching recording:', e);
      setExpandedVideoUrl(prev => ({ ...prev, [call.id]: 'none' }));
    } finally {
      setVideoLoading(null);
    }
  };

  const getNoteContent = (notes, type) => {
    if (type === 'summary') {
      const n = notes.find(n => n.note_type === 'ai_generated_summary') || notes.find(n => n.note_type === 'ai_summary');
      return n?.content || null;
    }
    const n = notes.find(n => n.note_type === 'ai_transcript');
    return n?.content || null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const groupMemories = (memories) => {
    const grouped = {};
    for (const m of memories || []) {
      if (!grouped[m.category]) grouped[m.category] = [];
      grouped[m.category].push(m);
    }
    return grouped;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Top Nav */}
      <Box component="header" sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, zIndex: 30 }}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h5" onClick={() => navigate('/')} sx={{ fontWeight: 800, cursor: 'pointer', background: 'linear-gradient(135deg, #2563EB, #7C3AED)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Vincula
              </Typography>
              <Button variant="text" size="small" startIcon={<ArrowBack sx={{ fontSize: 16 }} />} onClick={() => navigate('/dashboard/employee')} sx={{ color: 'text.secondary' }}>
                Dashboard
              </Button>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#EEF2FF', color: '#4F46E5', fontSize: '0.8rem' }}>
                {(user?.first_name?.[0] || 'A').toUpperCase()}
              </Avatar>
              <Button variant="outlined" size="small" onClick={logout} startIcon={<ExitToApp sx={{ fontSize: 18 }} />} sx={{ fontSize: '0.8rem', py: 0.75 }}>
                Salir
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Pacientes <WaveIcon width={28} height={28} color="#2563EB" />
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Buscá y consultá la información completa de cada paciente.
          </Typography>
        </Box>

        {/* Search */}
        <TextField
          fullWidth
          placeholder="Buscar por nombre, apellido o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading ? <CircularProgress size={20} /> : <Search sx={{ color: 'text.secondary' }} />}
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3, '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
        />

        {/* Patient list */}
        <Stack spacing={1.5}>
          {patients.length === 0 && !loading && (
            <Paper elevation={0} sx={{ textAlign: 'center', py: 8, border: '1px dashed', borderColor: '#E2E8F0', bgcolor: 'white' }}>
              <PersonOutline sx={{ fontSize: 48, color: '#CBD5E1', mb: 1.5 }} />
              <Typography variant="h5" color="text.secondary" fontWeight={500}>
                {search ? 'Sin resultados para esta búsqueda' : 'No hay pacientes registrados'}
              </Typography>
            </Paper>
          )}
          {patients.map((patient) => (
            <Paper
              key={patient.id}
              elevation={0}
              onClick={() => openPatientProfile(patient)}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'white',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#2563EB', boxShadow: '0 2px 8px rgba(37,99,235,0.08)' },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                  <Avatar sx={{ width: 44, height: 44, bgcolor: '#EFF6FF', color: '#2563EB', fontSize: '0.9rem', fontWeight: 600 }}>
                    {(patient.first_name?.[0] || '').toUpperCase()}{(patient.last_name?.[0] || '').toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body1" fontWeight={600} color="text.primary">
                        {patient.first_name} {patient.last_name}
                      </Typography>
                      <Chip
                        label={patient.is_active ? 'Activo' : 'Inactivo'}
                        size="small"
                        color={patient.is_active ? 'success' : 'default'}
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ mt: 0.25 }}>
                      <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Email sx={{ fontSize: 13 }} /> {patient.email}
                      </Typography>
                      {patient.phone && (
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone sx={{ fontSize: 13 }} /> {patient.phone}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Stack>
                <Stack alignItems="flex-end" spacing={0.5}>
                  <Chip label={`${patient.call_count} llamadas`} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                  {patient.last_call && (
                    <Typography variant="caption">Última: {formatDate(patient.last_call)}</Typography>
                  )}
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>

        <Box sx={{ height: { xs: 72, md: 0 } }} />
      </Container>

      <MobileBottomNav />

      {/* Patient Profile Dialog */}
      <Dialog open={!!selectedPatient} onClose={closeProfile} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ width: 44, height: 44, bgcolor: '#EFF6FF', color: '#2563EB', fontWeight: 600 }}>
                {(selectedPatient?.first_name?.[0] || '').toUpperCase()}{(selectedPatient?.last_name?.[0] || '').toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h4">{selectedPatient?.first_name} {selectedPatient?.last_name}</Typography>
                <Typography variant="caption">{selectedPatient?.email}</Typography>
              </Box>
            </Stack>
            <IconButton onClick={closeProfile}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          {profileLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : patientProfile ? (
            <>
              <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Tabs value={profileTab} onChange={(_, v) => setProfileTab(v)} sx={{ px: 3 }} variant="scrollable" scrollButtons="auto">
                  <Tab label="Datos" icon={<PersonOutline sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ minHeight: 48 }} />
                  <Tab label="Recuerdos" icon={<Psychology sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ minHeight: 48 }} />
                  <Tab label="Médico" icon={<MedicalServices sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ minHeight: 48 }} />
                  <Tab label="Llamadas" icon={<History sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ minHeight: 48 }} />
                  <Tab label="Alertas" icon={<Warning sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ minHeight: 48 }} />
                </Tabs>
              </Box>

              <Box sx={{ p: 3 }}>
                {/* Personal Data Tab */}
                {profileTab === 0 && (
                  <Box>
                    {!editingPatient ? (
                      <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="overline" color="text.secondary">Información personal</Typography>
                          <Button size="small" startIcon={<Edit sx={{ fontSize: 16 }} />} onClick={startEditing} sx={{ fontSize: '0.75rem' }}>
                            Editar
                          </Button>
                        </Stack>
                        {[
                          { label: 'Nombre', value: `${patientProfile.first_name} ${patientProfile.last_name}` },
                          { label: 'Email', value: patientProfile.email },
                          { label: 'Teléfono', value: patientProfile.phone },
                          { label: 'País', value: patientProfile.country },
                          { label: 'Ciudad', value: patientProfile.city },
                          { label: 'Dirección', value: patientProfile.address },
                          { label: 'Fecha de nacimiento', value: patientProfile.medical.date_of_birth ? formatDate(patientProfile.medical.date_of_birth) : null },
                          { label: 'Grupo sanguíneo', value: patientProfile.medical.blood_type },
                          { label: 'Contacto de emergencia', value: patientProfile.medical.emergency_contact_name ? `${patientProfile.medical.emergency_contact_name} — ${patientProfile.medical.emergency_contact_phone || ''}` : null },
                        ].map(({ label, value }) => (
                          <Box key={label}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary">{label}</Typography>
                            <Typography variant="body1">{value || '—'}</Typography>
                          </Box>
                        ))}
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="overline" color="text.secondary">Datos médicos</Typography>
                        {[
                          { label: 'Alergias', items: patientProfile.medical.allergies, color: '#DC2626', bg: '#FEF2F2' },
                          { label: 'Condiciones crónicas', items: patientProfile.medical.chronic_conditions, color: '#D97706', bg: '#FEF3C7' },
                          { label: 'Medicación actual', items: patientProfile.medical.current_medications, color: '#2563EB', bg: '#EFF6FF' },
                        ].map(({ label, items, color, bg }) => (
                          <Box key={label}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary">{label}</Typography>
                            {items && items.length > 0 ? (
                              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                                {items.map((item, i) => (
                                  <Chip key={i} label={item} size="small" sx={{ bgcolor: bg, color, fontWeight: 500 }} />
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">Sin datos</Typography>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="overline" color="text.secondary">Editando datos</Typography>
                          <Stack direction="row" spacing={1}>
                            <Button size="small" onClick={() => setEditingPatient(false)} sx={{ fontSize: '0.75rem' }}>Cancelar</Button>
                            <Button size="small" variant="contained" startIcon={savingPatient ? <CircularProgress size={14} /> : <Save sx={{ fontSize: 16 }} />} onClick={savePatientData} disabled={savingPatient} sx={{ fontSize: '0.75rem' }}>
                              Guardar
                            </Button>
                          </Stack>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <MuiTextField label="Nombre" value={editForm.first_name} onChange={(e) => setEditForm(f => ({ ...f, first_name: e.target.value }))} size="small" fullWidth />
                          <MuiTextField label="Apellido" value={editForm.last_name} onChange={(e) => setEditForm(f => ({ ...f, last_name: e.target.value }))} size="small" fullWidth />
                        </Stack>
                        <MuiTextField label="Teléfono" value={editForm.phone} onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))} size="small" fullWidth />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <MuiTextField label="País" value={editForm.country} onChange={(e) => setEditForm(f => ({ ...f, country: e.target.value }))} size="small" fullWidth />
                          <MuiTextField label="Ciudad" value={editForm.city} onChange={(e) => setEditForm(f => ({ ...f, city: e.target.value }))} size="small" fullWidth />
                        </Stack>
                        <MuiTextField label="Dirección" value={editForm.address} onChange={(e) => setEditForm(f => ({ ...f, address: e.target.value }))} size="small" fullWidth />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <MuiTextField label="Fecha de nacimiento" type="date" value={editForm.date_of_birth} onChange={(e) => setEditForm(f => ({ ...f, date_of_birth: e.target.value }))} size="small" fullWidth InputLabelProps={{ shrink: true }} />
                          <MuiTextField label="Grupo sanguíneo" value={editForm.blood_type} onChange={(e) => setEditForm(f => ({ ...f, blood_type: e.target.value }))} size="small" fullWidth placeholder="Ej: A+" />
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <MuiTextField label="Contacto de emergencia" value={editForm.emergency_contact_name} onChange={(e) => setEditForm(f => ({ ...f, emergency_contact_name: e.target.value }))} size="small" fullWidth />
                          <MuiTextField label="Tel. emergencia" value={editForm.emergency_contact_phone} onChange={(e) => setEditForm(f => ({ ...f, emergency_contact_phone: e.target.value }))} size="small" fullWidth />
                        </Stack>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="caption" color="text.secondary">Separar con comas</Typography>
                        <MuiTextField label="Alergias" value={editForm.allergies} onChange={(e) => setEditForm(f => ({ ...f, allergies: e.target.value }))} size="small" fullWidth placeholder="Ej: Penicilina, Maní" />
                        <MuiTextField label="Condiciones crónicas" value={editForm.chronic_conditions} onChange={(e) => setEditForm(f => ({ ...f, chronic_conditions: e.target.value }))} size="small" fullWidth placeholder="Ej: Diabetes, Hipertensión" />
                        <MuiTextField label="Medicación actual" value={editForm.current_medications} onChange={(e) => setEditForm(f => ({ ...f, current_medications: e.target.value }))} size="small" fullWidth placeholder="Ej: Metformina 500mg, Losartán 50mg" />
                      </Stack>
                    )}
                  </Box>
                )}

                {/* Memories Tab */}
                {profileTab === 1 && (
                  <Box>
                    {patientProfile.memories.length === 0 ? (
                      <Paper elevation={0} sx={{ textAlign: 'center', py: 6, border: '1px dashed', borderColor: '#E2E8F0' }}>
                        <Psychology sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                          Aún no hay recuerdos guardados de este paciente.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Se generan automáticamente después de cada llamada con el agente de IA.
                        </Typography>
                      </Paper>
                    ) : (
                      <>
                        {/* Monthly digest — top briefing card */}
                        {(() => {
                          const digest = patientProfile.memories.find(m => m.memory_type === 'digest');
                          if (!digest) return null;
                          return (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2.5,
                                mb: 2.5,
                                border: '1px solid',
                                borderColor: '#BFDBFE',
                                bgcolor: '#F0F7FF',
                                borderRadius: 2,
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <SummarizeOutlined sx={{ fontSize: 18, color: '#2563EB' }} />
                                <Typography variant="subtitle2" fontWeight={700} color="#2563EB">
                                  Resumen del mes
                                </Typography>
                                {digest.occurred_at && (
                                  <Typography variant="caption" color="text.secondary">
                                    — actualizado {new Date(digest.occurred_at).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })}
                                  </Typography>
                                )}
                              </Stack>
                              <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#334155' }}>
                                {digest.content || digest.value}
                              </Typography>
                            </Paper>
                          );
                        })()}

                        {/* Persistent memories — grouped by category */}
                        {(() => {
                          const persistent = patientProfile.memories.filter(m => m.memory_type !== 'episodic');
                          const grouped = {};
                          for (const m of persistent) {
                            if (!grouped[m.category]) grouped[m.category] = [];
                            grouped[m.category].push(m);
                          }
                          return Object.entries(grouped).length > 0 && (
                            <>
                              <Typography variant="overline" sx={{ color: '#64748B', mb: 1, display: 'block' }}>
                                Lo que sabemos
                              </Typography>
                              {Object.entries(grouped).map(([category, items]) => {
                                const config = CATEGORY_CONFIG[category] || { label: category, icon: <Psychology sx={{ fontSize: 18 }} />, color: '#64748B', bg: '#F8FAFC' };
                                return (
                                  <Accordion key={category} defaultExpanded elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1.5, '&:before': { display: 'none' } }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                      <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: config.bg, color: config.color }}>
                                          {config.icon}
                                        </Avatar>
                                        <Typography variant="h6">{config.label}</Typography>
                                        <Chip label={items.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                      </Stack>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <Stack spacing={0.5}>
                                        {items.map((mem, i) => (
                                          <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75, px: 1, borderRadius: 1, '&:hover': { bgcolor: '#F8FAFC' } }}>
                                            <Typography variant="body2" color="text.primary">
                                              {mem.content || `${mem.key}: ${mem.value}`}
                                            </Typography>
                                            <Chip
                                              label={`${Math.round(mem.confidence * 100)}%`}
                                              size="small"
                                              color={mem.confidence >= 0.8 ? 'success' : mem.confidence >= 0.5 ? 'warning' : 'default'}
                                              sx={{ height: 20, fontSize: '0.65rem', ml: 1, flexShrink: 0 }}
                                            />
                                          </Stack>
                                        ))}
                                      </Stack>
                                    </AccordionDetails>
                                  </Accordion>
                                );
                              })}
                            </>
                          );
                        })()}

                        {/* Episodic memories — grouped by date */}
                        {(() => {
                          const episodic = patientProfile.memories.filter(m => m.memory_type === 'episodic');
                          if (episodic.length === 0) return null;

                          const byDate = {};
                          const today = new Date(); today.setHours(0,0,0,0);
                          for (const m of episodic) {
                            const d = m.occurred_at ? new Date(m.occurred_at) : new Date(m.updated_at);
                            d.setHours(0,0,0,0);
                            const diff = Math.floor((today - d) / 86400000);
                            let label;
                            if (diff === 0) label = 'Hoy';
                            else if (diff === 1) label = 'Ayer';
                            else if (diff < 7) label = `Hace ${diff} días`;
                            else label = d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });

                            const key = m.occurred_at || m.updated_at;
                            if (!byDate[label]) byDate[label] = { items: [], sortKey: key };
                            byDate[label].items.push(m);
                          }

                          return (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="overline" sx={{ color: '#64748B', mb: 1.5, display: 'block' }}>
                                Últimos días
                              </Typography>
                              <Stack spacing={1.5}>
                                {Object.entries(byDate).map(([dateLabel, { items }]) => (
                                  <Paper key={dateLabel} elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2563EB', mb: 1 }}>
                                      {dateLabel}
                                    </Typography>
                                    <Stack spacing={0.75}>
                                      {items.map((mem, i) => {
                                        const config = CATEGORY_CONFIG[mem.category] || { color: '#64748B', bg: '#F8FAFC' };
                                        return (
                                          <Stack key={i} direction="row" alignItems="center" spacing={1}>
                                            <Chip
                                              label={(CATEGORY_CONFIG[mem.category] || {}).label || mem.category}
                                              size="small"
                                              sx={{ height: 20, fontSize: '0.6rem', bgcolor: config.bg, color: config.color, fontWeight: 600, flexShrink: 0 }}
                                            />
                                            <Typography variant="body2" color="text.primary">
                                              {mem.content || mem.value}
                                            </Typography>
                                          </Stack>
                                        );
                                      })}
                                    </Stack>
                                  </Paper>
                                ))}
                              </Stack>
                            </Box>
                          );
                        })()}
                      </>
                    )}
                  </Box>
                )}

                {/* Medical Tab */}
                {profileTab === 2 && (
                  <Stack spacing={2}>
                    {[
                      { label: 'Fecha de nacimiento', value: patientProfile.medical.date_of_birth ? formatDate(patientProfile.medical.date_of_birth) : null },
                      { label: 'Grupo sanguíneo', value: patientProfile.medical.blood_type },
                      { label: 'Contacto de emergencia', value: patientProfile.medical.emergency_contact_name ? `${patientProfile.medical.emergency_contact_name} — ${patientProfile.medical.emergency_contact_phone || ''}` : null },
                    ].map(({ label, value }) => (
                      <Box key={label}>
                        <Typography variant="caption" fontWeight={600}>{label}</Typography>
                        <Typography variant="body1">{value || '—'}</Typography>
                      </Box>
                    ))}

                    {[
                      { label: 'Alergias', items: patientProfile.medical.allergies, color: '#DC2626', bg: '#FEF2F2' },
                      { label: 'Condiciones crónicas', items: patientProfile.medical.chronic_conditions, color: '#D97706', bg: '#FEF3C7' },
                      { label: 'Medicación actual', items: patientProfile.medical.current_medications, color: '#2563EB', bg: '#EFF6FF' },
                    ].map(({ label, items, color, bg }) => (
                      <Box key={label}>
                        <Typography variant="caption" fontWeight={600}>{label}</Typography>
                        {items && items.length > 0 ? (
                          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                            {items.map((item, i) => (
                              <Chip key={i} label={item} size="small" sx={{ bgcolor: bg, color, fontWeight: 500 }} />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">Sin datos</Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}

                {/* Calls Tab */}
                {profileTab === 3 && (
                  <Box>
                    <Typography variant="caption" sx={{ mb: 2, display: 'block' }}>
                      Total: {patientProfile.total_calls} llamadas
                    </Typography>
                    {patientProfile.recent_calls.length === 0 ? (
                      <Paper elevation={0} sx={{ textAlign: 'center', py: 6, border: '1px dashed', borderColor: '#E2E8F0' }}>
                        <History sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">Sin llamadas registradas</Typography>
                      </Paper>
                    ) : (
                      <Stack spacing={1.5}>
                        {patientProfile.recent_calls.map((call) => {
                          const isExpanded = expandedCallId === call.id;
                          const callNotes = expandedNotes[call.id] || { loading: false, notes: [] };
                          const videoUrl = expandedVideoUrl[call.id];
                          const summary = getNoteContent(callNotes.notes, 'summary');
                          const transcript = getNoteContent(callNotes.notes, 'transcript');

                          return (
                            <Paper
                              key={call.id}
                              elevation={0}
                              sx={{
                                border: '1px solid',
                                borderColor: isExpanded ? '#93C5FD' : 'divider',
                                borderRadius: 2,
                                overflow: 'hidden',
                                transition: 'border-color 0.2s, box-shadow 0.2s',
                                ...(isExpanded && { boxShadow: '0 4px 20px rgba(37,99,235,0.08)' }),
                                '&:hover': { borderColor: '#93C5FD' },
                              }}
                            >
                              {/* Collapsed header — clickable */}
                              <Box
                                onClick={() => toggleCallExpand(call)}
                                sx={{
                                  p: 2,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  bgcolor: isExpanded ? '#F8FAFF' : 'white',
                                  transition: 'background-color 0.2s',
                                  '&:hover': { bgcolor: '#F8FAFF' },
                                }}
                              >
                                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
                                  <Chip
                                    label={call.status === 'completed' ? 'Completada' : call.status === 'active' ? 'En curso' : call.status}
                                    size="small"
                                    color={call.status === 'completed' ? 'success' : call.status === 'active' ? 'info' : 'default'}
                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                  />
                                  {call.employee_name ? (
                                    <Typography variant="body2">con {call.employee_name}</Typography>
                                  ) : (
                                    <Typography variant="body2">Llamada con IA</Typography>
                                  )}
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Typography variant="caption" color="text.secondary">{formatDateTime(call.started_at)}</Typography>
                                  {call.duration_seconds > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <AccessTime sx={{ fontSize: 12 }} /> {Math.floor(call.duration_seconds / 60)}m
                                    </Typography>
                                  )}
                                  {isExpanded ? <ExpandLess sx={{ color: '#64748B' }} /> : <ExpandMore sx={{ color: '#94A3B8' }} />}
                                </Stack>
                              </Box>

                              {/* Expanded detail */}
                              <Collapse in={isExpanded} timeout={300}>
                                <Divider />
                                {callNotes.loading ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress size={28} />
                                  </Box>
                                ) : (
                                  <Box sx={{ p: 0 }}>
                                    {/* Two-column layout: Video + Summary */}
                                    <Stack direction={{ xs: 'column', md: 'row' }} sx={{ minHeight: 200 }}>
                                      {/* Left: Video */}
                                      <Box sx={{
                                        width: { xs: '100%', md: '45%' },
                                        bgcolor: '#0F172A',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        minHeight: { xs: 200, md: 240 },
                                      }}>
                                        {videoUrl && videoUrl !== 'none' ? (
                                          <video
                                            src={videoUrl}
                                            controls
                                            style={{ width: '100%', height: '100%', maxHeight: 280, outline: 'none' }}
                                            controlsList="nodownload"
                                          />
                                        ) : videoUrl === 'none' ? (
                                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4 }}>
                                            <PlayArrow sx={{ fontSize: 36, color: 'rgba(255,255,255,0.3)' }} />
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                              No se encontró video para esta llamada
                                            </Typography>
                                          </Box>
                                        ) : (
                                          <Box
                                            onClick={(e) => { e.stopPropagation(); loadVideoForCall(call); }}
                                            sx={{
                                              cursor: 'pointer',
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'center',
                                              gap: 1.5,
                                              py: 4,
                                            }}
                                          >
                                            <Box sx={{
                                              width: 56, height: 56,
                                              borderRadius: '50%',
                                              bgcolor: 'rgba(255,255,255,0.15)',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              transition: 'all 0.2s',
                                              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', transform: 'scale(1.1)' },
                                            }}>
                                              {videoLoading === call.id
                                                ? <CircularProgress size={24} sx={{ color: 'white' }} />
                                                : <PlayArrow sx={{ fontSize: 32, color: 'white' }} />
                                              }
                                            </Box>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                              {videoLoading === call.id ? 'Cargando...' : 'Click para reproducir'}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>

                                      {/* Right: Summary */}
                                      <Box sx={{
                                        flex: 1,
                                        p: 2.5,
                                        overflow: 'auto',
                                        maxHeight: { md: 280 },
                                      }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                          <SummarizeOutlined sx={{ fontSize: 18, color: '#2563EB' }} />
                                          <Typography variant="subtitle2" fontWeight={700} color="#2563EB">
                                            Resumen
                                          </Typography>
                                        </Stack>
                                        {summary ? (
                                          <Typography variant="body2" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap', color: '#334155' }}>
                                            {summary}
                                          </Typography>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            No hay resumen disponible para esta llamada.
                                          </Typography>
                                        )}
                                      </Box>
                                    </Stack>

                                    {/* Bottom: Transcript */}
                                    <Divider />
                                    <Box sx={{ p: 2.5 }}>
                                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                        <Article sx={{ fontSize: 18, color: '#7C3AED' }} />
                                        <Typography variant="subtitle2" fontWeight={700} color="#7C3AED">
                                          Transcripción
                                        </Typography>
                                      </Stack>
                                      {transcript ? (
                                        <Paper
                                          elevation={0}
                                          sx={{
                                            p: 2,
                                            maxHeight: 250,
                                            overflow: 'auto',
                                            bgcolor: '#F8FAFC',
                                            border: '1px solid',
                                            borderColor: '#E2E8F0',
                                            borderRadius: 1.5,
                                            fontSize: '0.82rem',
                                            lineHeight: 1.9,
                                            whiteSpace: 'pre-wrap',
                                            color: '#475569',
                                            fontFamily: '"Inter", -apple-system, sans-serif',
                                          }}
                                        >
                                          {transcript}
                                        </Paper>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                          No hay transcripción disponible para esta llamada.
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                )}
                              </Collapse>
                            </Paper>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                )}

                {/* Alerts Tab */}
                {profileTab === 4 && (
                  <Box>
                    {patientProfile.alerts.length === 0 ? (
                      <Paper elevation={0} sx={{ textAlign: 'center', py: 6, border: '1px dashed', borderColor: '#E2E8F0' }}>
                        <CheckCircle sx={{ fontSize: 48, color: '#10B981', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">Sin alertas. Todo parece estar bien.</Typography>
                      </Paper>
                    ) : (
                      <Stack spacing={1.5}>
                        {patientProfile.alerts.map((alert) => (
                          <MuiAlert
                            key={alert.id}
                            severity={alert.severity === 'urgent' ? 'error' : alert.severity === 'warning' ? 'warning' : 'info'}
                            sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}
                          >
                            <Typography variant="body2" fontWeight={600}>{alert.alert_type}</Typography>
                            <Typography variant="body2">{alert.content}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatDateTime(alert.created_at)}</Typography>
                          </MuiAlert>
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body1" color="text.secondary">Error al cargar el perfil</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
